/*
 * mk-configurator: sauna add-on builder that replaces the Avis Product Options app.
 *
 * Data: one JSON file per option set (assets/mk-addons-<id>.json, generated by
 * Muskoka/addons-data/convert.py). Prices and availability always come from the
 * live Shopify products (`/products/<handle>.js` under the market root), never from
 * the stored copy in the JSON.
 *
 * Cart: the sauna and its add-ons are added in one request. Line properties follow
 * the Avis convention (_has_apo / _apo_order / _apo_option) so the cart drawer and
 * cart page keep grouping a configured sauna with its add-ons.
 */
(() => {
  if (customElements.get('mk-configurator')) return;

  const norm = (text) => String(text ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

  // Guided steps; each group carries its step from convert.py.
  const STEPS = [
    { id: 'design', title: 'Design', intro: 'Windows, finishes and upgrades for your sauna.' },
    { id: 'heat', title: 'Heat', intro: 'Your stove or heater, and what it needs to run.' },
    { id: 'comfort', title: 'Accessories', intro: 'Lighting and the extras you will reach for every session.' },
    { id: 'delivery', title: 'Delivery', intro: 'Build it yourself, or let our team deliver and assemble it.' },
  ];

  const baseLabel = (label) =>
    label
      .replace(/^(select|choose)\s+(your\s+)?/i, '')
      .replace(/\s+for\s+.+$/i, '')
      .toLowerCase();

  // The main stove question reads as "heat source" on the page (Nano, Oct 13).
  // Display only: the cart and order keep the original Avis label.
  const isHeatSource = (label) => ['stove', 'stove options'].includes(baseLabel(label));
  const shortLabel = (label) => (isHeatSource(label) ? 'your heat source' : baseLabel(label));
  const displayLabel = (label) => (isHeatSource(label) ? 'Choose Your Heat Source' : label);

  const money = (cents) => {
    const format = window.FoxTheme?.settings?.moneyFormat;
    if (window.FoxTheme?.Currency?.formatMoney && format) {
      // The shop format wraps amounts in <span class=money>; this renders as text.
      return FoxTheme.Currency.formatMoney(cents, format).replace(/<[^>]*>/g, '');
    }
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Shopify CDN images can be requested at any width.
  const largeImage = (src) => {
    try {
      const url = new URL(src, window.location.href);
      if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(url.href)) return src;
      url.searchParams.set('width', '1200');
      return url.href;
    } catch (error) {
      return src;
    }
  };

  const productCache = new Map();
  const fetchProduct = (root, handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  class MkConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.root = this.dataset.root || window.Shopify?.routes?.root || '/';
      this.form = this.closest('form');
      this.groupsElement = this.querySelector('[data-mk-groups]');
      this.selections = new Map();
      this.load().catch((error) => {
        console.error('mk-configurator: could not load add-ons', error);
        this.groupsElement.innerHTML =
          '<p class="mk-cfg__notice">We could not load the build options. Please refresh the page or contact us.</p>';
      });
    }

    async load() {
      const urls = JSON.parse(this.dataset.sets || '[]');
      const imageAssetBase = new URL(urls[0], window.location.href);
      const sets = await Promise.all(urls.map((url) => fetch(url).then((response) => response.json())));
      const groups = sets.flatMap((set) => set.groups.map((group) => ({ ...group, set: set.id })));

      const handles = new Set([this.dataset.handle]);
      groups.forEach((group) => (group.values || []).forEach((value) => value.handle && handles.add(value.handle)));
      const products = new Map(
        await Promise.all([...handles].map(async (handle) => [handle, await fetchProduct(this.root, handle)]))
      );
      this.product = products.get(this.dataset.handle);

      // Keep only values whose product/variant is live and available, like Avis did.
      this.groups = groups
        .map((group) => {
          if (group.kind === 'divider') return group;
          const values = (group.values || [])
            .map((value) => {
              if (value.countries && !value.countries.includes(window.Shopify?.country)) return null;
              // Relative cutout filenames live beside this theme's option JSON.
              if (value.img) value = { ...value, img: new URL(value.img, imageAssetBase).href };
              if (!value.variant) return { ...value, price: 0, img: value.img || products.get(value.handle)?.featured_image };
              const variant = products.get(value.handle)?.variants?.find((v) => v.id === value.variant);
              if (!variant || !variant.available) return null;
              const image = value.hide_image ? null : value.img || variant.featured_image?.src || products.get(value.handle)?.featured_image;
              return { ...value, price: variant.price, img: image };
            })
            .filter(Boolean);
          return { ...group, values };
        })
        .filter((group) => group.kind === 'divider' || group.values.length);

      this.byKey = new Map(this.groups.map((group) => [group.key, group]));
      this.linkSubGroups();
      // Uku type cards have no product of their own; use their actual colour option.
      const ukuTypes = this.byKey.get('h-control');
      ukuTypes?.values.forEach((value, index) => {
        if (value.img) return;
        const colourGroup = (this.unlocks.get(`${ukuTypes.key}|${index}`) || [])
          .find((group) => ['h-uku-local', 'h-uku-wifi'].includes(group.key));
        value.img = colourGroup?.values.find((colour) => colour.img)?.img;
      });
      await this.restoreFromCart();
      this.render();
      this.bindForm();
      this.update();
    }

    // ---- Sub-choices ---------------------------------------------------------

    // A value that reveals more groups (e.g. a light that then needs a colour) is a parent
    // of those groups. Its card shows "From" the cheapest complete choice, never "Included".
    linkSubGroups() {
      this.unlocks = new Map();
      this.groups.forEach((group) => {
        if (group.kind === 'divider' || group.rule?.action !== 'show') return;
        // "any" rules list every value that opens the group; "all" rules hinge on the last one.
        const conditions = group.rule.mode === 'all' ? group.rule.when.slice(-1) : group.rule.when;
        conditions.forEach((condition) => {
          const parent = this.byKey.get(condition.group);
          if (!parent || parent === group || parent.kind === 'divider') return;
          const wanted = norm(condition.value);
          parent.values.forEach((value, index) => {
            const label = norm(value.label);
            if (condition.op === 'is' ? label !== wanted : !label.includes(wanted)) return;
            const key = `${parent.key}|${index}`;
            if (!this.unlocks.has(key)) this.unlocks.set(key, []);
            if (!this.unlocks.get(key).includes(group)) this.unlocks.get(key).push(group);
          });
          group.parent = group.parent || parent;
        });
      });
    }

    unlockedRequired(group, index) {
      return (this.unlocks.get(`${group.key}|${index}`) || []).filter((g) => g.required && g.values.length);
    }

    // Cheapest way to complete a required group, following chained sub-choices
    // (e.g. electric stove -> control type -> controller colour).
    minCost(group, depth = 0) {
      if (depth > 4) return 0;
      return Math.min(
        ...group.values.map(
          (value, index) =>
            value.price + this.unlockedRequired(group, index).reduce((sum, g) => sum + this.minCost(g, depth + 1), 0)
        )
      );
    }

    // What the current choices in a group add, including the sub-choices they opened.
    chosenCost(group, depth = 0) {
      if (depth > 4) return 0;
      return (this.selections.get(group.key) || []).reduce(
        (sum, index) =>
          sum +
          group.values[index].price +
          this.unlockedRequired(group, index).reduce((s, g) => s + this.chosenCost(g, depth + 1), 0),
        0
      );
    }

    // Price shown on a value card: its own price plus what its required sub-choices add.
    priceLabel(group, index) {
      const value = group.values[index];
      const subs = this.unlockedRequired(group, index);
      if (!subs.length) return value.price > 0 ? `+${money(value.price)}` : value.note || 'Included';
      const picked = subs.flatMap((g) => (this.selections.get(g.key) || []).map((i) => g.values[i]));
      const chosen = (this.selections.get(group.key) || []).includes(index);
      if (chosen && picked.length && subs.every((g) => (this.selections.get(g.key) || []).length)) {
        const total = value.price + subs.reduce((sum, g) => sum + this.chosenCost(g), 0);
        return `${total > 0 ? `+${money(total)}` : 'Included'} · ${picked.map((v) => v.label).join(', ')}`;
      }
      const from = value.price + subs.reduce((sum, g) => sum + this.minCost(g), 0);
      return from > 0 ? `From +${money(from)}` : 'Choose next';
    }

    // ---- Rules -------------------------------------------------------------

    selectedLabels(key) {
      const group = this.byKey.get(key);
      if (!group || !this.visible.has(key)) return [];
      return (this.selections.get(key) || []).map((index) => group.values[index]?.label).filter(Boolean);
    }

    // Product variant title of the sauna itself (e.g. "59"D x 59"W (Up to 2 person)").
    variantTitle() {
      const variant = this.product?.variants?.find((v) => v.id === this.variantId()) || this.product?.variants?.[0];
      return variant?.title || '';
    }

    matches(condition) {
      // Avis "avis_shopify_option" rules: show a group only for one size of the sauna.
      const selected =
        condition.group === '@variant' ? [norm(this.variantTitle())] : this.selectedLabels(condition.group).map(norm);
      const wanted = norm(condition.value);
      if (condition.op === 'is') return selected.includes(wanted);
      return selected.some((label) => label.includes(wanted));
    }

    isVisible(group) {
      if (!group.rule) return true;
      const results = group.rule.when.map((condition) => this.matches(condition));
      const hit = group.rule.mode === 'all' ? results.every(Boolean) : results.some(Boolean);
      return group.rule.action === 'hide' ? !hit : hit;
    }

    // Groups depend on earlier choices; repeat until nothing changes.
    computeVisibility() {
      this.visible = new Set(this.groups.map((group) => group.key));
      for (let pass = 0; pass < 6; pass++) {
        let changed = false;
        this.groups.forEach((group) => {
          if (group.kind === 'divider') return;
          const show = this.isVisible(group);
          if (show !== this.visible.has(group.key)) {
            changed = true;
            show ? this.visible.add(group.key) : this.visible.delete(group.key);
          }
        });
        if (!changed) break;
      }
      // A hidden group never keeps a selection.
      this.groups.forEach((group) => {
        if (!this.visible.has(group.key)) this.selections.delete(group.key);
      });
    }

    // ---- Rendering -----------------------------------------------------------

    render() {
      // Dividers travel with the group that follows them.
      this.groups.forEach((group, index) => {
        if (group.kind !== 'divider') return;
        const next = this.groups.slice(index + 1).find((g) => g.kind !== 'divider');
        group.step = next?.step || 'design';
      });

      const used = STEPS.filter((step) => this.groups.some((g) => g.kind !== 'divider' && g.step === step.id));
      this.steps = used.length > 1 ? used : [];
      const fragment = document.createDocumentFragment();

      if (!this.steps.length) {
        this.groups.forEach((group) => fragment.appendChild(this.renderItem(group)));
        this.groupsElement.replaceChildren(fragment);
        return;
      }

      this.steps.forEach((step) => {
        const section = document.createElement('section');
        section.className = 'mk-cfg-step';
        section.dataset.step = step.id;
        section.innerHTML = `
          <button type="button" class="mk-cfg-step__head" aria-expanded="false" aria-controls="mk-cfg-step-${step.id}">
            <span class="mk-cfg-step__num" aria-hidden="true"></span>
            <span class="mk-cfg-step__titles"><span class="mk-cfg-step__title"></span><span class="mk-cfg-step__status"></span></span>
            <span class="mk-cfg-step__chev" aria-hidden="true"></span>
          </button>
          <div class="mk-cfg-step__body" id="mk-cfg-step-${step.id}" hidden>
            <p class="mk-cfg-step__intro"></p>
            <div class="mk-cfg-step__groups"></div>
            <button type="button" class="mk-cfg-step__next"></button>
          </div>`;
        section.querySelector('.mk-cfg-step__title').textContent = step.title;
        section.querySelector('.mk-cfg-step__intro').textContent = step.intro;
        const groups = section.querySelector('.mk-cfg-step__groups');
        this.groups.filter((g) => g.step === step.id).forEach((group) => groups.appendChild(this.renderItem(group)));
        section.querySelector('.mk-cfg-step__head').addEventListener('click', () =>
          this.openStep(this.openStepId === step.id ? null : step.id)
        );
        section.querySelector('.mk-cfg-step__next').addEventListener('click', () => this.goToNextStep(step.id));
        fragment.appendChild(section);
      });
      this.groupsElement.replaceChildren(fragment);
      this.openStep(this.steps[0].id, false);
    }

    renderItem(group) {
      if (group.kind !== 'divider') return this.renderGroup(group);
      const heading = document.createElement('p');
      heading.className = 'mk-cfg__divider';
      heading.dataset.key = group.key;
      heading.textContent = group.label;
      return heading;
    }

    visibleSteps() {
      return this.steps.filter((step) =>
        this.groups.some((g) => g.kind !== 'divider' && g.step === step.id && this.visible.has(g.key))
      );
    }

    openStep(id, scroll = true) {
      this.openStepId = id;
      this.groupsElement.querySelectorAll('.mk-cfg-step').forEach((section) => {
        const open = section.dataset.step === id;
        section.classList.toggle('is-open', open);
        section.querySelector('.mk-cfg-step__head').setAttribute('aria-expanded', open ? 'true' : 'false');
        section.querySelector('.mk-cfg-step__body').hidden = !open;
        if (open && scroll) {
          const top = section.getBoundingClientRect().top + window.scrollY - 90;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      });
    }

    goToNextStep(id) {
      const visible = this.visibleSteps();
      const next = visible[visible.findIndex((step) => step.id === id) + 1];
      if (next) {
        this.openStep(next.id);
      } else {
        this.openStep(null, false);
        this.querySelector('[data-mk-summary]').scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    updateSteps() {
      if (!this.steps.length) return;
      const visible = this.visibleSteps();
      this.steps.forEach((step) => {
        const section = this.groupsElement.querySelector(`.mk-cfg-step[data-step="${step.id}"]`);
        const position = visible.indexOf(step);
        section.hidden = position < 0;
        if (position < 0) return;

        const groups = this.groups.filter(
          (g) => g.kind !== 'divider' && g.step === step.id && this.visible.has(g.key)
        );
        const missing = groups.filter((g) => this.missing.includes(g));
        const picked = groups.flatMap((g) => (this.selections.get(g.key) || []).map((i) => g.values[i].label));
        const status = section.querySelector('.mk-cfg-step__status');
        if (missing.length) {
          status.textContent = `Choose ${missing.map((g) => shortLabel(g.label)).join(', ')}`;
        } else if (picked.length) {
          status.textContent = picked.join(' · ');
        } else {
          status.textContent = 'Optional';
        }
        section.classList.toggle('is-done', !missing.length && picked.length > 0);
        section.classList.toggle('is-missing', missing.length > 0);
        section.querySelector('.mk-cfg-step__num').textContent = position + 1;
        const next = visible[position + 1];
        section.querySelector('.mk-cfg-step__next').textContent = next ? `Continue to ${next.title}` : 'Review your build';
      });
    }

    renderGroup(group) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'mk-cfg__group';
      fieldset.dataset.key = group.key;

      const legend = document.createElement('legend');
      legend.className = 'mk-cfg__legend';
      legend.innerHTML = `<span class="mk-cfg__name"></span>${
        group.required ? '<span class="mk-cfg__req">Required</span>' : '<span class="mk-cfg__opt">Optional</span>'
      }<span class="mk-cfg__picked" aria-live="polite"></span>`;
      legend.querySelector('.mk-cfg__name').textContent = displayLabel(group.label);
      if (group.parent) {
        fieldset.classList.add('is-sub');
        const forLabel = document.createElement('span');
        forLabel.className = 'mk-cfg__for';
        legend.appendChild(forLabel);
      }
      fieldset.appendChild(legend);

      if (group.help) {
        const help = document.createElement('p');
        help.className = 'mk-cfg__help';
        help.textContent = group.help;
        fieldset.appendChild(help);
      }

      const list = document.createElement('div');
      list.className = 'mk-cfg__values';
      list.setAttribute('role', group.kind === 'multi' ? 'group' : 'radiogroup');
      list.setAttribute('aria-label', displayLabel(group.label));
      group.values.forEach((value, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mk-cfg__value';
        button.dataset.index = index;
        button.setAttribute('role', group.kind === 'multi' ? 'checkbox' : 'radio');
        button.innerHTML = `${value.img ? '<span class="mk-cfg__img"><img alt="" loading="lazy" width="160" height="160"></span>' : ''}<span class="mk-cfg__label"></span><span class="mk-cfg__price"></span><span class="mk-cfg__tick" aria-hidden="true"></span>`;
        if (value.img) button.querySelector('img').src = value.img;
        button.querySelector('.mk-cfg__label').textContent = value.label;
        button.querySelector('.mk-cfg__price').textContent = this.priceLabel(group, index);
        button.addEventListener('click', () => this.toggle(group, index));
        list.appendChild(button);
      });
      fieldset.appendChild(list);

      const error = document.createElement('p');
      error.className = 'mk-cfg__error';
      error.hidden = true;
      error.textContent = `Please choose ${shortLabel(group.label)}.`;
      fieldset.appendChild(error);
      return fieldset;
    }

    toggle(group, index) {
      const current = this.selections.get(group.key) || [];
      let next;
      if (group.kind === 'multi') {
        next = current.includes(index) ? current.filter((i) => i !== index) : [...current, index];
        if (group.max && next.length > group.max) next = next.slice(-group.max);
      } else if (current.includes(index)) {
        next = group.required ? current : [];
      } else {
        next = [index];
      }
      next.length ? this.selections.set(group.key, next) : this.selections.delete(group.key);
      this.update();
      if (next.includes(index) && !current.includes(index)) this.preview(group.values[index]);
    }

    // ---- Gallery preview -------------------------------------------------------

    // Shows the chosen option over the main product photo (the app could not do this).
    preview(value) {
      if (!value?.img) return;
      const wrap = document.querySelector('media-gallery .product__media-gallery-viewer-wrap');
      if (!wrap) return;
      if (!this.previewElement) {
        const figure = document.createElement('figure');
        figure.className = 'mk-cfg-preview';
        figure.innerHTML = `<img alt=""><figcaption><span class="mk-cfg-preview__name"></span><span class="mk-cfg-preview__price"></span></figcaption><button type="button" class="mk-cfg-preview__close">Back to photos</button>`;
        figure.querySelector('button').addEventListener('click', () => this.hidePreview());
        wrap.appendChild(figure);
        // Any interaction with the gallery itself returns to the product photos.
        document
          .querySelector('media-gallery .product__media-gallery-thumbails')
          ?.addEventListener('click', () => this.hidePreview());
        this.previewElement = figure;
      }
      const image = this.previewElement.querySelector('img');
      image.src = largeImage(value.img);
      image.alt = value.label;
      this.previewElement.querySelector('.mk-cfg-preview__name').textContent = value.label;
      this.previewElement.querySelector('.mk-cfg-preview__price').textContent =
        value.price > 0 ? `+${money(value.price)}` : value.note || 'Included';
      this.previewElement.classList.add('is-on');
    }

    hidePreview() {
      this.previewElement?.classList.remove('is-on');
    }

    update() {
      this.computeVisibility();
      let addons = 0;
      let pending = 0;
      const missing = [];

      this.groups.forEach((group) => {
        const element = this.groupsElement.querySelector(`[data-key="${group.key}"]`);
        if (!element) return;
        const visible = this.visible.has(group.key);
        element.hidden = !visible;
        if (group.kind === 'divider' || !visible) return;

        const picked = this.selections.get(group.key) || [];
        element.querySelectorAll('.mk-cfg__value').forEach((button) => {
          const on = picked.includes(Number(button.dataset.index));
          button.classList.toggle('is-on', on);
          button.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        const pickedValues = picked.map((index) => group.values[index]);
        element.querySelector('.mk-cfg__picked').textContent = pickedValues.map((value) => value.label).join(', ');
        element.querySelectorAll('.mk-cfg__value').forEach((button) => {
          button.querySelector('.mk-cfg__price').textContent = this.priceLabel(group, Number(button.dataset.index));
        });
        const forLabel = element.querySelector('.mk-cfg__for');
        if (forLabel) {
          const parentPick = (this.selections.get(group.parent.key) || []).map((i) => group.parent.values[i].label);
          forLabel.textContent = parentPick.length ? `For ${parentPick.join(', ')}` : '';
        }
        pickedValues.forEach((value) => (addons += value.price));

        if (group.required && picked.length < (group.min || 1)) {
          missing.push(group);
          pending += this.minCost(group);
        }
        if (picked.length) element.querySelector('.mk-cfg__error').hidden = true;
      });

      // Dividers only show when a group under them is visible.
      this.groups.forEach((group, index) => {
        if (group.kind !== 'divider') return;
        const element = this.groupsElement.querySelector(`[data-key="${group.key}"]`);
        let any = false;
        for (let i = index + 1; i < this.groups.length && this.groups[i].kind !== 'divider'; i++) {
          if (this.visible.has(this.groups[i].key)) any = true;
        }
        element.hidden = !any;
      });

      this.missing = missing;
      this.addonsTotal = addons;
      this.updateSteps();
      this.renderSummary(addons, pending);
    }

    // On the product page these come from the product form; in the cart editor from data attributes.
    variantId() {
      return Number(this.form?.querySelector('[name="id"]')?.value || this.dataset.variantId);
    }

    quantity() {
      return Number(this.form?.querySelector('[name="quantity"]')?.value || this.dataset.quantity) || 1;
    }

    basePrice() {
      const id = this.variantId();
      const variant = this.product?.variants?.find((v) => v.id === id) || this.product?.variants?.[0];
      return variant?.price || 0;
    }

    renderSummary(addons, pending) {
      const base = this.basePrice();
      const total = base + addons;
      const summary = this.querySelector('[data-mk-summary]');
      summary.querySelector('[data-mk-total]').textContent = money(total);
      summary.querySelector('[data-mk-addons]').textContent = addons > 0 ? `${money(base)} + ${money(addons)} in options` : '';
      const status = summary.querySelector('[data-mk-status]');
      if (this.missing.length) {
        status.textContent = `Complete from ${money(total + pending)} · ${this.missing.length} required ${
          this.missing.length === 1 ? 'choice' : 'choices'
        } left`;
        status.classList.remove('is-done');
      } else {
        status.textContent = 'Your build is complete';
        status.classList.add('is-done');
      }
      // The sticky add-to-cart bar reads the options total from this element.
      const legacy = this.querySelector('.avp-productoption-totalpriceadd');
      if (legacy) legacy.textContent = money(addons);
      this.dispatchEvent(new CustomEvent('mk:configurator-change', { bubbles: true, detail: { base, addons, total } }));
    }

    // ---- Cart ----------------------------------------------------------------

    // ---- Send to a specialist ----------------------------------------------------

    buildSummaryText() {
      const lines = [`Build request: ${this.product?.title || this.dataset.handle}`];
      const variant = this.product?.variants?.find((v) => v.id === this.variantId());
      if (variant && variant.title !== 'Default Title') lines.push(`Variant: ${variant.title}`);
      this.groups.forEach((group) => {
        if (group.kind === 'divider' || !this.visible.has(group.key)) return;
        (this.selections.get(group.key) || []).forEach((index) => {
          const value = group.values[index];
          // Every line says what it costs, so the specialist sees the same numbers as the shopper.
          // A choice that opens a required sub-choice is priced on that sub-choice's line.
          const price =
            value.price > 0
              ? ` (+${money(value.price)})`
              : this.unlockedRequired(group, index).length
                ? ''
                : ` (${value.note ? value.note.toLowerCase() : 'no extra cost'})`;
          lines.push(`- ${group.cart}: ${value.label}${price}`);
        });
      });
      if (this.missing?.length) lines.push(`Still to choose: ${this.missing.map((g) => shortLabel(g.label)).join(', ')}`);
      const currency = window.Shopify?.currency?.active || '';
      lines.push(`Options: ${money(this.addonsTotal || 0)} · Build total: ${money(this.basePrice() + (this.addonsTotal || 0))} ${currency}`.trim());
      lines.push(`Page: ${window.location.origin}${window.location.pathname}`);
      return lines.join('\n');
    }

    quotePrefill() {
      const total = this.basePrice() + (this.addonsTotal || 0);
      const p = this.product || {};
      return {
        product: { handle: p.handle || this.dataset.handle, title: p.title, vendor: p.vendor, type: p.type },
        total,
        banner: `${this.addonsTotal ? 'Your build' : 'You were viewing'}: ${p.title} · ${money(total)}.`,
      };
    }

    hasChoices() {
      return [...this.selections.values()].some((picked) => picked.length);
    }

    sendToSpecialist() {
      openQuote(this.buildSummaryText(), 'configurator_build', this.dataset.handle, this.quotePrefill());
      (window.dataLayer = window.dataLayer || []).push({
        event: 'configurator_send_build',
        product_handle: this.dataset.handle,
        build_total: (this.basePrice() + (this.addonsTotal || 0)) / 100,
        build_complete: !this.missing?.length,
      });
    }

    bindForm() {
      this.querySelector('[data-mk-send-build]')?.addEventListener('click', () => this.sendToSpecialist());
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event), { capture: true });
      document.addEventListener('variant:changed', () => this.update());
    }

    // Cart editor: replace the configured lines and refresh the cart in place.
    async saveEdit(button) {
      if (this.missing?.length) {
        this.showMissing();
        return false;
      }
      button?.setAttribute('aria-busy', 'true');
      try {
        // Add the new build first, then drop the old lines, so a failed add loses nothing.
        const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        const response = await fetch(`${FoxTheme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ items: this.buildItems() }),
        });
        const added = await response.json();
        if (!response.ok || added.status) throw new Error(added.description || 'Could not update the cart');

        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );
        const updates = Object.fromEntries((this.editKeys || []).map((key) => [key, 0]));
        const cart = await (
          await fetch(`${FoxTheme.routes.cart_update_url || `${this.root}cart/update`}.js`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
          })
        ).json();
        this.editKeys = null;
        if (document.body.classList.contains('cart-template') || /\/cart\/?$/.test(window.location.pathname)) {
          window.location.reload();
          return true;
        }
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        return true;
      } catch (error) {
        console.error('mk-configurator: could not save the build', error);
        return false;
      } finally {
        button?.removeAttribute('aria-busy');
      }
    }

    showMissing() {
      this.missing.forEach((group) => {
        const element = this.groupsElement.querySelector(`[data-key="${group.key}"] .mk-cfg__error`);
        if (element) element.hidden = false;
      });
      if (this.steps.length) this.openStep(this.missing[0].step, false);
      const first = this.groupsElement.querySelector(`[data-key="${this.missing[0].key}"]`);
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first?.querySelector('.mk-cfg__value')?.focus({ preventScroll: true });
    }

    buildItems() {
      const order = String(Date.now());
      const quantity = this.quantity();
      const parentProperties = { _has_apo: 'true', _apo_order: order };
      const children = [];
      // Groups whose choice opens a paid sub-choice that carries the price: the cart shows "—" for them.
      const grouping = [];
      // $0 choices with their own wording (e.g. "Shipping at checkout"): the cart shows it instead of "Included".
      const notes = [];

      this.groups.forEach((group) => {
        if (group.kind === 'divider' || !this.visible.has(group.key)) return;
        const picked = (this.selections.get(group.key) || []).map((index) => group.values[index]);
        if (!picked.length) return;
        parentProperties[group.cart] = picked.map((value) => value.label).join(', ');
        const opensPaid = (index) => this.unlockedRequired(group, index).some((sub) => this.chosenCost(sub) > 0);
        if ((this.selections.get(group.key) || []).some(opensPaid)) {
          grouping.push(group.cart);
        }
        const note = picked.find((value) => value.note && !value.variant)?.note;
        if (note) notes.push(`${group.cart}=${note}`);
        picked
          .filter((value) => value.variant)
          .forEach((value) =>
            children.push({
              id: value.variant,
              quantity,
              properties: { _apo_option: `${group.cart}: ${value.label}`, _apo_order: `Added by: ${order}` },
            })
          );
      });

      if (grouping.length) parentProperties._mk_parents = grouping.join(' | ');
      if (notes.length) parentProperties._mk_notes = notes.join(' | ');
      const main = { id: this.variantId(), quantity, properties: parentProperties };
      return [main, ...children];
    }

    async onSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.missing?.length) {
        this.showMissing();
        return;
      }

      const button = this.form.querySelector('[type="submit"]');
      if (button?.hasAttribute('aria-disabled')) return;
      button?.setAttribute('aria-disabled', 'true');
      button?.classList.add('btn--loading');

      try {
        if (this.editKeys?.length) await this.removeLines(this.editKeys);

        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );
        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({ items: this.buildItems(), sections, sections_url: window.location.pathname });

        // Reuse the theme's own add-to-cart flow (cart state, events, drawer).
        if (typeof this.form.handleFormSubmission === 'function') {
          this.form.formData = new FormData(this.form);
          this.form.lastSubmittedElement = event.submitter || button;
          this.form.handleFormSubmission(config);
          this.editKeys = null;
        } else {
          const response = await fetch(`${FoxTheme.routes.cart_add_url}`, config);
          if (!response.ok) throw new Error('Could not add to cart');
          window.location.href = FoxTheme.routes.cart_url;
        }
      } catch (error) {
        console.error('mk-configurator: add to cart failed', error);
        button?.classList.remove('btn--loading');
        button?.removeAttribute('aria-disabled');
      }
    }

    // ---- Edit from cart (?mk_edit=<line key>) ---------------------------------

    async restoreFromCart() {
      const key = this.dataset.editKey || new URLSearchParams(window.location.search).get('mk_edit');
      if (!key) return;
      const cart = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .catch(() => null);
      const parent = cart?.items?.find((item) => item.key === key && item.properties?._has_apo === 'true');
      if (!parent) return;

      const order = String(parent.properties._apo_order);
      const children = cart.items.filter((item) => String(item.properties?._apo_order) === `Added by: ${order}`);
      const variants = new Set(children.map((item) => item.variant_id));

      this.groups.forEach((group) => {
        if (group.kind === 'divider') return;
        const stored = norm(parent.properties[group.cart]);
        const picked = [];
        group.values.forEach((value, index) => {
          if (value.variant ? variants.has(value.variant) : stored && stored.split(', ').includes(norm(value.label))) {
            picked.push(index);
          }
        });
        if (picked.length) this.selections.set(group.key, group.kind === 'multi' ? picked : [picked[0]]);
      });
      this.editKeys = [parent.key, ...children.map((item) => item.key)];
      const quantityInput = this.form?.querySelector('[name="quantity"]');
      if (quantityInput) quantityInput.value = parent.quantity;
      this.querySelector('[data-mk-editing]')?.removeAttribute('hidden');
    }

    async removeLines(keys) {
      const updates = Object.fromEntries(keys.map((key) => [key, 0]));
      await fetch(`${this.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }
  }

  customElements.define('mk-configurator', MkConfigurator);

  // ---- Shared: quote modal, cart summary, cart editor ----------------------------

  const configuratorMarkup = () => `
    <div class="mk-cfg__groups" data-mk-groups><p class="mk-cfg__loading">Loading build options…</p></div>
    <div class="mk-cfg__summary" data-mk-summary aria-live="polite">
      <div class="mk-cfg__summary-row">
        <span class="mk-cfg__summary-label">Your build</span>
        <span class="mk-cfg__summary-total" data-mk-total></span>
      </div>
      <p class="mk-cfg__summary-addons" data-mk-addons></p>
      <p class="mk-cfg__summary-status" data-mk-status></p>
    </div>`;

  // The mk-form quote modal: use the page's dialog, or build it from the cart template.
  function quoteDialog() {
    const existing = document.getElementById('mk-quote-dialog');
    if (existing) return existing;
    const template = document.querySelector('template[data-mk-quote-template]');
    if (!template || !window.__mkFormLoaded) return null;
    const dialog = template.content.firstElementChild.cloneNode(true);
    document.body.appendChild(dialog);
    // mk-form initialises forms inside a node on this event (same as the theme editor).
    dialog.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    return dialog;
  }

  // ---- Quote prefill: answer what the page already knows, keep every step editable ----

  let factsPromise;
  const saunaFacts = () => {
    if (!factsPromise) {
      factsPromise = window.MkFactsUrl
        ? fetch(window.MkFactsUrl).then((r) => (r.ok ? r.json() : {})).catch(() => ({}))
        : Promise.resolve({});
    }
    return factsPromise;
  };

  // product: { handle, title, vendor, type } — values match the options in mk-form-configs.js.
  function quoteAnswers(product, facts) {
    const type = norm(product.type);
    const title = norm(product.title);
    const vendor = norm(product.vendor);
    const fact = facts[product.handle] || {};
    const answers = {};

    if (type.includes('cold') || title.includes('plunge')) {
      answers.mk_product_category = 'Cold Plunge';
      if (title.includes('barracuda')) answers.mk_products_interested = 'Barracuda X';
      else if (title.includes('nordic')) answers.mk_products_interested = 'Nordic Vitality Hot and Cold Tubs';
      else if (title.includes('shower')) answers.mk_products_interested = 'Outdoor Showers';
      return answers;
    }
    if (type.includes('heater') || type.includes('stove')) {
      answers.mk_product_category = 'Sauna Heater / Stove';
      answers.mk_products_interested = type.includes('wood') ? 'Wood-Burning Stoves' : 'Electric Heaters';
      return answers;
    }
    if (!type.includes('sauna')) return answers;

    const indoor =
      fact.place === 'Indoor' ||
      (fact.place !== 'Outdoor' &&
        (title.includes('indoor') || title.includes('infrared') || vendor === 'taive' || vendor === 'finnmark'));
    answers.mk_product_category = indoor ? 'Indoor Sauna' : 'Outdoor Sauna';
    const family = [
      [vendor === 'saunum', 'Saunum Saunas'],
      [vendor === 'saunalife', 'Saunalife Saunas'],
      [vendor === 'leil' || title.includes('leil'), 'Leil Saunas'],
      [vendor === 'auroom', 'Auroom Saunas'],
      [vendor === 'taive', 'Taive Saunas'],
      [title.includes('infrared') || vendor === 'finnmark', 'Infrared Saunas'],
      [title.includes('barrel'), 'Barrel Saunas'],
      [title.includes('luna'), 'Luna Saunas'],
      [indoor, 'Cedar Saunas'],
      [title.includes('cube') || title.includes('cabin'), 'Cube and Cabin Saunas'],
      [true, 'Canadian Made Cedar Saunas'],
    ].find(([match]) => match);
    answers.mk_products_interested = family[1];
    if (fact.cap) {
      answers.mk_capacity =
        fact.cap <= 2 ? '1 to 2 people' : fact.cap <= 4 ? '3 to 4 people' : fact.cap <= 6 ? '5 to 6 people' : '7 or more people';
    }
    return answers;
  }

  // Budget ranges read like "Under $10,000", "$10,000 to $15,000", "$25,000 and up".
  function budgetFor(options, totalCents) {
    const total = totalCents / 100;
    return options.find((option) => {
      const text = norm(option.t || option.v);
      const numbers = (text.match(/\$?\s?([\d,]+)/g) || []).map((n) => Number(n.replace(/[^\d]/g, '')));
      if (!numbers.length) return false;
      if (text.startsWith('under')) return total < numbers[0];
      if (text.includes('and up')) return total >= numbers[0];
      return numbers.length > 1 && total >= numbers[0] && total < numbers[1];
    })?.v;
  }

  function prefillQuote(dialog, prefill) {
    const instance = dialog.querySelector('[data-mk-form]')?.__mk;
    if (!instance) return;

    const apply = (step) =>
      step.fields.forEach((field) => {
        if (!field.inputs) return;
        const key = field.def.key;
        const wanted =
          key === 'mk_budget' && prefill.total != null ? budgetFor(field.def.options || [], prefill.total) : prefill.answers[key];
        if (!wanted) return;
        const match = field.inputs.find((input) => norm(input.value) === norm(wanted));
        if (!match) return;
        field.inputs.forEach((input) => {
          if (!field.def.multi) input.checked = input === match;
          else if (input === match) input.checked = true;
          input.parentNode.classList.toggle('is-checked', input.checked);
        });
      });

    // Steps that depend on the category are rebuilt as the shopper moves on: re-apply there.
    if (!instance.__mkPrefill) {
      const rebuild = instance.rebuildStep.bind(instance);
      instance.rebuildStep = (index, category) => {
        rebuild(index, category);
        if (instance.__mkPrefill) apply(instance.steps[index]);
      };
    }
    instance.__mkPrefill = prefill;
    instance.steps.forEach(apply);
    instance.history = [];
    instance.go(0, true);

    let banner = dialog.querySelector('.mk-quote-prefill');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'mk-quote-prefill';
      banner.setAttribute('role', 'status');
      dialog.querySelector('[data-mk-form]').before(banner);
    }
    banner.innerHTML = '<strong></strong> <span>We filled in what we know. Review each answer and change anything.</span>';
    banner.querySelector('strong').textContent = prefill.banner;
  }

  // Fills the quote modal (summary in the message, prefilled answers). Returns the dialog.
  async function prepareQuote(text, handle, prefill) {
    const dialog = quoteDialog();
    const message = dialog?.querySelector('textarea[name="message"]');
    if (!dialog || !message) return null;
    if (handle !== undefined) dialog.dataset.product = handle;
    if (prefill?.product) {
      prefill.answers = quoteAnswers(prefill.product, await saunaFacts());
      prefillQuote(dialog, prefill);
    }
    setMessage(message, text);
    return dialog;
  }

  function setMessage(message, text) {
    if (!message || !text) return;
    // Replace an earlier summary, keep anything the shopper typed after it.
    const previous = message.value.split('\n\n---\n')[1] || '';
    message.value = `${text}${previous ? `\n\n---\n${previous}` : ''}`;
    message.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // ---- Cart drawer -> quote page: carry the cart into the page's form ----------------

  const CARRY_KEY = 'mkQuoteCarry';

  async function carryToQuotePage(link) {
    try {
      const [text, prefill] = await Promise.all([cartSummaryText(), cartPrefill()]);
      if (prefill?.product) prefill.answers = quoteAnswers(prefill.product, await saunaFacts());
      sessionStorage.setItem(CARRY_KEY, JSON.stringify({ text, prefill, at: Date.now() }));
    } catch (error) {
      // Storage blocked or cart unavailable: the page still opens, just without prefill.
    }
    window.location.href = link.href;
  }

  function applyCarry() {
    let carry;
    try {
      carry = JSON.parse(sessionStorage.getItem(CARRY_KEY) || 'null');
    } catch (error) {
      return;
    }
    if (!carry || Date.now() - carry.at > 30 * 60 * 1000) return;
    const root = document.querySelector('[data-mk-form="quote"][data-location="page"]');
    if (!root) return;
    let tries = 0;
    const wait = setInterval(() => {
      if (!root.__mk && ++tries < 50) return;
      clearInterval(wait);
      if (!root.__mk) return;
      if (carry.prefill?.answers) prefillQuote(root.parentElement, carry.prefill);
      setMessage(root.querySelector('textarea[name="message"]'), carry.text);
      try {
        sessionStorage.removeItem(CARRY_KEY);
      } catch (error) {
        /* nothing to clean up */
      }
    }, 100);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyCarry);
  else applyCarry();

  async function openQuote(text, cta, handle, prefill) {
    const dialog = await prepareQuote(text, handle, prefill);
    if (!dialog) {
      window.location.href = `${window.Shopify?.routes?.root || '/'}pages/contact-lead-gen`;
      return;
    }
    // mk-form opens the modal (and tracks the click) for any [data-mk-open-quote].
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.hidden = true;
    trigger.setAttribute('data-mk-open-quote', '');
    trigger.setAttribute('data-mk-internal', '');
    trigger.dataset.cta = cta;
    document.body.appendChild(trigger);
    trigger.click();
    trigger.remove();
  }

  async function cartSummaryText() {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const children = (order) => cart.items.filter((i) => String(i.properties?._apo_order) === `Added by: ${order}`);
    const childKeys = new Set();
    cart.items
      .filter((i) => i.properties?._has_apo === 'true')
      .forEach((p) => children(p.properties._apo_order).forEach((c) => childKeys.add(c.key)));

    const lines = [`Cart request (${cart.currency}):`];
    cart.items.forEach((item) => {
      if (childKeys.has(item.key)) return;
      lines.push(`${item.quantity} x ${item.product_title}${item.variant_title ? ` (${item.variant_title})` : ''}: ${money(item.final_line_price)}`);
      if (item.properties?._has_apo !== 'true') return;
      let total = item.final_line_price;
      const kids = children(item.properties._apo_order);
      const grouping = String(item.properties._mk_parents || '').split(' | ');
      const notes = new Map(
        String(item.properties._mk_notes || '')
          .split(' | ')
          .filter(Boolean)
          .map((entry) => [entry.slice(0, entry.indexOf('=')), entry.slice(entry.indexOf('=') + 1)])
      );
      const priceNote = (name, price) =>
        price > 0
          ? ` (+${money(price)})`
          : grouping.includes(name)
            ? ''
            : ` (${notes.has(name) ? notes.get(name).toLowerCase() : 'included'})`;
      Object.entries(item.properties).forEach(([name, value]) => {
        if (name.startsWith('_') || !value) return;
        const priced = kids.filter((c) => String(c.properties._apo_option).startsWith(`${name}: `));
        if (priced.length) {
          priced.forEach((c) =>
            lines.push(`  - ${c.properties._apo_option}${priceNote(name, c.final_line_price)}`)
          );
        } else {
          lines.push(`  - ${name}: ${value}${String(value).includes('(+') ? '' : priceNote(name, 0)}`);
        }
      });
      kids.forEach((c) => (total += c.final_line_price));
      lines.push(`  Configuration total: ${money(total)}`);
    });
    lines.push(`Cart total: ${money(cart.total_price)}`);
    return lines.join('\n');
  }

  // The item a specialist should start from: the most expensive sauna or build in the cart.
  async function cartPrefill() {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const groupTotal = (item) =>
      cart.items
        .filter((i) => String(i.properties?._apo_order) === `Added by: ${item.properties?._apo_order}`)
        .reduce((sum, i) => sum + i.final_line_price, item.final_line_price);
    const lines = cart.items.filter((i) => !i.properties?._apo_option);
    const main = [...lines].sort((a, b) => groupTotal(b) - groupTotal(a))[0];
    if (!main) return null;
    const others = lines.length - 1;
    return {
      product: { handle: main.handle, title: main.product_title, vendor: main.vendor, type: main.product_type },
      total: cart.total_price,
      banner: `Your cart: ${main.product_title}${others > 0 ? ` + ${others} more` : ''} · ${money(cart.total_price)}.`,
    };
  }

  function openEditor(button) {
    const dialog = document.createElement('dialog');
    dialog.className = 'mk-cfg-dialog';
    dialog.setAttribute('aria-label', 'Edit your build');
    dialog.innerHTML = `
      <div class="mk-cfg-dialog__panel">
        <header class="mk-cfg-dialog__head">
          <div><p class="mk-cfg-dialog__eyebrow">Edit your build</p><h2 class="mk-cfg-dialog__title"></h2></div>
          <button type="button" class="mk-cfg-dialog__close" aria-label="Close">&times;</button>
        </header>
        <div class="mk-cfg-dialog__body"></div>
        <footer class="mk-cfg-dialog__foot">
          <p class="mk-cfg-dialog__error" role="alert" hidden>We could not save your changes. Please try again.</p>
          <button type="button" class="mk-cfg-dialog__save">Save changes</button>
        </footer>
      </div>`;
    dialog.querySelector('.mk-cfg-dialog__title').textContent = button.dataset.mkTitle || '';

    const cfg = document.createElement('mk-configurator');
    cfg.className = 'mk-cfg';
    Object.assign(cfg.dataset, {
      handle: button.dataset.mkHandle,
      sets: button.dataset.mkSets,
      root: window.Shopify?.routes?.root || '/',
      variantId: button.dataset.mkVariant,
      quantity: button.dataset.mkQuantity,
      editKey: button.dataset.mkKey,
    });
    cfg.innerHTML = configuratorMarkup();
    dialog.querySelector('.mk-cfg-dialog__body').appendChild(cfg);

    const close = () => {
      dialog.close();
      dialog.remove();
    };
    dialog.querySelector('.mk-cfg-dialog__close').addEventListener('click', close);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    const save = dialog.querySelector('.mk-cfg-dialog__save');
    save.addEventListener('click', async () => {
      save.disabled = true;
      const error = dialog.querySelector('.mk-cfg-dialog__error');
      error.hidden = true;
      const ok = await cfg.saveEdit(save);
      save.disabled = false;
      if (ok) close();
      else if (!cfg.missing?.length) error.hidden = false;
    });

    document.body.appendChild(dialog);
    dialog.showModal();
  }

  document.addEventListener('click', (event) => {
    const edit = event.target.closest?.('[data-mk-edit-build]');
    if (edit) {
      event.preventDefault();
      openEditor(edit);
      return;
    }
    const toggle = event.target.closest?.('[data-mk-bd-toggle]');
    if (toggle) {
      const open = toggle.closest('[data-mk-bd]').classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }
    const quote = event.target.closest?.('[data-mk-cart-quote]');
    if (quote?.matches('a[href]')) {
      event.preventDefault();
      carryToQuotePage(quote);
      return;
    }
    if (quote) {
      event.preventDefault();
      Promise.all([cartSummaryText(), cartPrefill()])
        .then(([text, prefill]) => openQuote(text, quote.dataset.cta || 'cart', '', prefill))
        .catch(() => openQuote('', quote.dataset.cta || 'cart', ''));
    }
  });

  // Nano's own quote buttons on a product page: prefill with the sauna being viewed.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest?.('[data-mk-open-quote]');
      if (!trigger || trigger.hasAttribute('data-mk-internal')) return;
      const cfg = document.querySelector('mk-configurator');
      if (!cfg?.product) return;
      prepareQuote(cfg.hasChoices() ? cfg.buildSummaryText() : '', cfg.dataset.handle, cfg.quotePrefill());
    },
    true
  );

  window.MkBuild = { openQuote, openEditor, cartSummaryText };
})();
